import { useState } from 'react'
import { Box, Text, Stack, Table, Button, Checkbox, Select, Alert, Group } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCheck } from '@tabler/icons-react'
import { useJiraCustomFields } from '@/hooks/useJiraData'
import { jiraApi, type JiraCustomField, type JiraCustomFieldMapping } from '@/api/jira'
import { useCurrentTenant } from '@/store/auth'
import { useQueryClient } from '@tanstack/react-query'

interface SelectedField {
  field: JiraCustomField
  direction: string
}

const DIRECTION_OPTIONS = [
  { value: 'from_jira', label: '← Jira' },
  { value: 'to_jira', label: '→ Jira' },
  { value: 'both', label: '↔ Bidirecional' },
]

export function JiraCustomFieldsPanel() {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  const { data: fields } = useJiraCustomFields()
  const [selected, setSelected] = useState<Map<string, SelectedField>>(new Map())
  const [saving, setSaving] = useState(false)
  const [discovering, setDiscovering] = useState(false)
  const [discovered, setDiscovered] = useState<JiraCustomField[]>([])
  const queryClient = useQueryClient()

  const handleDiscover = async () => {
    setDiscovering(true)
    try {
      if (fields) setDiscovered(fields)
    } finally {
      setDiscovering(false)
    }
  }

  const toggleField = (field: JiraCustomField) => {
    const next = new Map(selected)
    if (next.has(field.id)) {
      next.delete(field.id)
    } else {
      next.set(field.id, { field, direction: 'from_jira' })
    }
    setSelected(next)
  }

  const handleSave = async () => {
    if (!tenantId) return
    setSaving(true)
    try {
      const mappings: JiraCustomFieldMapping[] = Array.from(selected.values()).map(s => ({
        jira_field_id: s.field.id,
        jira_field_name: s.field.name,
        target: 'raw_fields',
        direction: s.direction,
      }))
      await jiraApi.upsertCustomFieldMappings(tenantId, mappings)
      queryClient.invalidateQueries({ queryKey: ['jira-custom-fields'] })
      notifications.show({ title: 'Salvo', message: 'Mapeamentos de campos salvos', color: 'green' })
    } catch {
      notifications.show({ title: 'Erro', message: 'Falha ao salvar mapeamentos de campos', color: 'red' })
    }
    setSaving(false)
  }

  const displayFields = discovered.length > 0 ? discovered : (fields ?? [])
  const overLimit = selected.size > 20

  return (
    <Box>
      <Text fw={600} size="sm" mb="sm">Campos Customizados</Text>
      <Stack gap="sm">
        <Button
          size="xs"
          onClick={handleDiscover}
          loading={discovering}
          variant="light"
        >
          Descobrir Campos
        </Button>

        {displayFields.length > 0 && (
          <>
            {overLimit && (
              <Alert color="red">
                <Text size="xs">Máximo de 20 campos customizados permitidos. Remova {selected.size - 20} campo(s).</Text>
              </Alert>
            )}

            <Table verticalSpacing="xs" fz="xs" striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={40}></Table.Th>
                  <Table.Th>Campo</Table.Th>
                  <Table.Th w={120}>Tipo</Table.Th>
                  <Table.Th w={160}>Direção</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {displayFields.slice(0, 50).map(f => (
                  <Table.Tr key={f.id}>
                    <Table.Td>
                      <Checkbox
                        size="xs"
                        checked={selected.has(f.id)}
                        onChange={() => toggleField(f)}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{f.name}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">{f.type}</Text>
                    </Table.Td>
                    <Table.Td>
                      {selected.has(f.id) && (
                        <Select
                          size="xs"
                          data={DIRECTION_OPTIONS}
                          value={selected.get(f.id)!.direction}
                          onChange={(v) => {
                            if (v) {
                              const next = new Map(selected)
                              next.set(f.id, { ...next.get(f.id)!, direction: v })
                              setSelected(next)
                            }
                          }}
                        />
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            <Group justify="flex-end">
              <Button
                size="xs"
                onClick={handleSave}
                loading={saving}
                disabled={selected.size === 0 || overLimit}
                leftSection={<IconCheck size={14} />}
              >
                Salvar Mapeamentos ({selected.size})
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Box>
  )
}
